/**
 * VenueBookingSheet — slide-in booking widget for venue pages.
 *
 * Adaptive shell that handles all three Eventism offering kinds inline,
 * so visitors can buy tickets / book experiences / reserve a table
 * without leaving the venue page. Pattern blends:
 *   • DesignMyNight: sidebar/sheet with kind-aware CTA + 3-stage flow
 *   • Tock: prepaid tier picker for experiences
 *   • OpenTable: date / party-size / time triplet for reservations
 *
 * Stripe is invoked via the existing endpoints
 *   POST /api/events/{event_id}/purchase                  (events)
 *   POST /api/payments/experience-booking/checkout        (experiences)
 *   POST /api/payments/reservation/checkout               (reservations)
 * which all return `{ checkout_url }` (paid) or `{ free: true, ... }`
 * (instant confirm). We redirect on paid, toast on free.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Loader2, Ticket, Utensils, Sparkles, Minus, Plus, Calendar,
  Users, Clock, ChevronLeft, CheckCircle2, CreditCard,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ──────────────────────────────────────────────────────────
const fmtMoney = (cents) =>
  typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${period}`;
};

// CTA labels by offering kind. Mirrors the DesignMyNight "Book / Enquire"
// vocabulary plus Tock's "Reserve Experience" framing.
const ctaForKind = (kind) => {
  if (kind === 'experience')  return 'Reserve Experience';
  if (kind === 'reservation') return 'Book a Table';
  return 'Get Tickets';
};

const iconForKind = (kind) => {
  if (kind === 'experience')  return Sparkles;
  if (kind === 'reservation') return Utensils;
  return Ticket;
};

// Today as YYYY-MM-DD for date inputs.
const todayIso = () => new Date().toISOString().slice(0, 10);

// ──────────────────────────────────────────────────────────────────────
//  Sub-flow: Event ticket purchase
// ──────────────────────────────────────────────────────────────────────
function EventTicketFlow({ event, onClose }) {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState({}); // { [ticket_type_id]: count }
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/events/${event.event_id}/ticket-types`);
        if (!cancelled) {
          const tiers = (res.data?.ticket_types || res.data || []).filter(
            (t) => t.is_active !== false,
          );
          setTicketTypes(tiers);
        }
      } catch {
        if (!cancelled) setTicketTypes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [event.event_id]);

  const bumpQty = (ttId, delta) => {
    setQty((prev) => {
      const tt = ticketTypes.find((t) => t.ticket_type_id === ttId);
      if (!tt) return prev;
      const current = prev[ttId] || 0;
      const minQ = Math.max(1, tt.min_per_order || 1);
      const remaining = (tt.quantity_total || 0) - (tt.quantity_sold || 0);
      const maxQ = Math.min(tt.max_per_order || 10, remaining);
      let next = current + delta;
      if (delta > 0 && current === 0 && minQ > 1) next = Math.min(maxQ, minQ);
      if (delta < 0 && next > 0 && next < minQ) next = 0;
      return { ...prev, [ttId]: Math.max(0, Math.min(maxQ, next)) };
    });
  };

  const totalQty = Object.values(qty).reduce((s, v) => s + v, 0);
  const totalPrice = ticketTypes.reduce(
    (sum, tt) => sum + (Number(tt.price) || 0) * (qty[tt.ticket_type_id] || 0),
    0,
  );

  const handlePurchase = async () => {
    if (!buyer.name.trim() || !buyer.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    const items = Object.entries(qty)
      .filter(([, q]) => q > 0)
      .map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
    if (items.length === 0) {
      toast.error('Please select at least one ticket');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/purchase`, {
        name: buyer.name.trim(),
        email: buyer.email.trim(),
        phone: buyer.phone.trim() || undefined,
        items,
      });
      if (res.data?.free || res.data?.success) {
        toast.success('Tickets confirmed — check your email');
        onClose?.();
      } else if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Checkout could not be started');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-7 w-7 animate-spin text-fuchsia-500" />
      </div>
    );
  }
  if (ticketTypes.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        Tickets aren&apos;t set up for this event yet. Please check the event page.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Select Tickets</p>
        {ticketTypes.map((tt) => {
          const remaining = (tt.quantity_total || 0) - (tt.quantity_sold || 0);
          const soldOut = remaining <= 0;
          const current = qty[tt.ticket_type_id] || 0;
          return (
            <div
              key={tt.ticket_type_id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3"
              data-testid={`tt-row-${tt.ticket_type_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-sm">{tt.name}</h4>
                  {soldOut && <Badge variant="outline" className="text-red-500 border-red-500/40 text-[10px]">Sold out</Badge>}
                </div>
                {tt.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{tt.description}</p>}
                <p className="text-sm font-bold text-fuchsia-400 mt-1">${Number(tt.price).toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="icon" variant="outline"
                  className="h-8 w-8"
                  onClick={() => bumpQty(tt.ticket_type_id, -1)}
                  disabled={soldOut || current === 0}
                  data-testid={`tt-minus-${tt.ticket_type_id}`}
                ><Minus className="h-3.5 w-3.5" /></Button>
                <span className="w-6 text-center font-bold text-sm" data-testid={`tt-qty-${tt.ticket_type_id}`}>{current}</span>
                <Button
                  size="icon" variant="outline"
                  className="h-8 w-8"
                  onClick={() => bumpQty(tt.ticket_type_id, 1)}
                  disabled={soldOut}
                  data-testid={`tt-plus-${tt.ticket_type_id}`}
                ><Plus className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      {totalQty > 0 && (
        <>
          <div className="space-y-3 pt-2 border-t border-border">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Your Details</p>
            <div className="space-y-2">
              <div>
                <Label htmlFor="vbs-name" className="text-xs">Full name</Label>
                <Input id="vbs-name" data-testid="vbs-event-name" value={buyer.name}
                  onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label htmlFor="vbs-email" className="text-xs">Email</Label>
                <Input id="vbs-email" type="email" data-testid="vbs-event-email" value={buyer.email}
                  onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label htmlFor="vbs-phone" className="text-xs">Phone (optional)</Label>
                <Input id="vbs-phone" data-testid="vbs-event-phone" value={buyer.phone}
                  onChange={(e) => setBuyer((b) => ({ ...b, phone: e.target.value }))} className="h-9" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">{totalQty} ticket{totalQty === 1 ? '' : 's'}</p>
              <p className="text-lg font-bold">${totalPrice.toFixed(2)}</p>
            </div>
            <Button
              className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold"
              onClick={handlePurchase}
              disabled={submitting}
              data-testid="vbs-event-checkout"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
              Continue to Checkout
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Sub-flow: Experience slot booking
// ──────────────────────────────────────────────────────────────────────
function ExperienceFlow({ event, onClose }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotId, setSlotId] = useState(null);
  const [guests, setGuests] = useState(2);
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/experiences/${event.event_id}/slots`);
        if (!cancelled) setSlots(res.data?.slots || []);
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [event.event_id]);

  const selectedSlot = useMemo(
    () => slots.find((s) => s.slot_id === slotId),
    [slots, slotId],
  );
  const totalCents = selectedSlot ? (selectedSlot.price_cents || 0) * guests : 0;

  const handleBook = async () => {
    if (!selectedSlot) { toast.error('Pick a date & time first'); return; }
    if (!buyer.name.trim() || !buyer.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    if (guests > selectedSlot.available) {
      toast.error(`Only ${selectedSlot.available} spot(s) left at that time`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/experience-booking/checkout`, {
        event_id: event.event_id,
        slot_id: selectedSlot.slot_id,
        guest_count: guests,
        name: buyer.name.trim(),
        email: buyer.email.trim(),
        phone: buyer.phone.trim() || undefined,
        notes: buyer.notes.trim() || undefined,
        origin_url: window.location.origin,
      });
      if (res.data?.free) {
        toast.success('Experience booked — check your email');
        onClose?.();
      } else if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Checkout could not be started');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-7 w-7 animate-spin text-fuchsia-500" />
      </div>
    );
  }
  if (slots.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        No upcoming slots are open for this experience yet.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Pick a Date & Time</p>
        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-1">
          {slots.map((s) => {
            const isSel = s.slot_id === slotId;
            const soldOut = s.available <= 0;
            return (
              <button
                key={s.slot_id}
                type="button"
                onClick={() => !soldOut && setSlotId(s.slot_id)}
                disabled={soldOut}
                data-testid={`vbs-slot-${s.slot_id}`}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  isSel ? 'border-fuchsia-500 bg-fuchsia-500/10' :
                  soldOut ? 'border-border opacity-50 cursor-not-allowed' :
                  'border-border hover:border-fuchsia-400/60'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">{fmtDate(s.date)} · {fmtTime(s.start_time)}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtMoney(s.price_cents)} per guest · {soldOut ? 'Sold out' : `${s.available} left`}
                    </p>
                  </div>
                  {isSel && <CheckCircle2 className="h-4 w-4 text-fuchsia-400 flex-shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedSlot && (
        <>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Party Size</p>
            <div className="flex items-center gap-3">
              <Button size="icon" variant="outline" className="h-9 w-9"
                onClick={() => setGuests((g) => Math.max(1, g - 1))}
                data-testid="vbs-exp-guests-minus">
                <Minus className="h-4 w-4" />
              </Button>
              <span className="font-bold text-lg w-8 text-center" data-testid="vbs-exp-guests">{guests}</span>
              <Button size="icon" variant="outline" className="h-9 w-9"
                onClick={() => setGuests((g) => Math.min(selectedSlot.available || 20, g + 1))}
                data-testid="vbs-exp-guests-plus">
                <Plus className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground ml-2">{selectedSlot.available} available</span>
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-border">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Your Details</p>
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Full name</Label>
                <Input data-testid="vbs-exp-name" value={buyer.name}
                  onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" data-testid="vbs-exp-email" value={buyer.email}
                  onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Phone (optional)</Label>
                <Input data-testid="vbs-exp-phone" value={buyer.phone}
                  onChange={(e) => setBuyer((b) => ({ ...b, phone: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Notes (allergies, accessibility…)</Label>
                <Textarea data-testid="vbs-exp-notes" value={buyer.notes}
                  onChange={(e) => setBuyer((b) => ({ ...b, notes: e.target.value }))}
                  className="min-h-[60px]" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">{guests} guest{guests === 1 ? '' : 's'}</p>
              <p className="text-lg font-bold">{totalCents > 0 ? fmtMoney(totalCents) : 'Free'}</p>
            </div>
            <Button
              className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold"
              onClick={handleBook}
              disabled={submitting}
              data-testid="vbs-exp-checkout"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> :
                totalCents > 0 ? <CreditCard className="h-4 w-4 mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {totalCents > 0 ? 'Continue to Checkout' : 'Confirm Booking'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Sub-flow: Reservation (Tock-style — party → date strip → time grid)
// ──────────────────────────────────────────────────────────────────────

// Generate a HH:MM grid between two HH:MM bounds at `stepMin` granularity.
const buildTimeGrid = (startHHMM, endHHMM, stepMin = 15) => {
  const [sh, sm] = String(startHHMM).split(':').map(Number);
  const [eh, em] = String(endHHMM).split(':').map(Number);
  if (Number.isNaN(sh) || Number.isNaN(eh)) return [];
  let cur = sh * 60 + (sm || 0);
  const end = eh * 60 + (em || 0);
  const out = [];
  while (cur <= end) {
    const h = Math.floor(cur / 60);
    const m = cur % 60;
    out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    cur += stepMin;
  }
  return out;
};

// Date strip — next N days starting today, like Tock's horizontal day-picker.
const buildDateStrip = (count = 14) => {
  const out = [];
  const base = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    out.push({
      iso,
      day: d.toLocaleDateString('en-US', { weekday: 'short' }),
      num: d.getDate(),
      mon: d.toLocaleDateString('en-US', { month: 'short' }),
    });
  }
  return out;
};

function ReservationFlow({ event, onClose }) {
  const gridTimes = useMemo(() => {
    if (event.available_times && event.available_times.length) {
      return event.available_times;
    }
    const start = event.event_time || '11:00';
    const end = event.end_time || '22:00';
    return buildTimeGrid(start, end, 15);
  }, [event.available_times, event.event_time, event.end_time]);

  const dates = useMemo(() => buildDateStrip(14), []);

  const partyMin = Number(event.party_size_min) || 1;
  const partyMax = Number(event.party_size_max) || 8;
  const depositPerGuestCents = Number(event.deposit_amount_cents) || 0;
  const largeThreshold = Number(event.large_party_threshold) || 0;

  const [step, setStep] = useState('search');
  const [date, setDate] = useState(dates[0].iso);
  const [party, setParty] = useState(Math.max(2, partyMin));
  const [time, setTime] = useState(null);
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '', seating_area: '', special_requests: '' });
  const [submitting, setSubmitting] = useState(false);

  const depositActive = depositPerGuestCents > 0 &&
    (!largeThreshold || party >= largeThreshold);
  const depositTotalCents = depositActive ? depositPerGuestCents * party : 0;

  const handleReserve = async () => {
    if (!time) { toast.error('Pick a time slot first'); return; }
    if (!buyer.name.trim() || !buyer.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    if (party < partyMin || party > partyMax) {
      toast.error(`Party size must be between ${partyMin} and ${partyMax}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/reservation/checkout`, {
        event_id: event.event_id,
        date,
        start_time: time,
        party_size: party,
        name: buyer.name.trim(),
        email: buyer.email.trim(),
        phone: buyer.phone.trim() || undefined,
        seating_area: buyer.seating_area.trim() || undefined,
        special_requests: buyer.special_requests.trim() || undefined,
        origin_url: window.location.origin,
      });
      if (res.data?.free) {
        toast.success('Reservation confirmed — check your email');
        onClose?.();
      } else if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Reservation could not be created');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reservation failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'search') {
    return (
      <div className="space-y-5">
        {/* Tock-style offer summary card */}
        <div className="rounded-lg border border-border bg-card p-3 flex gap-3">
          {event.image_url && (
            <img src={event.image_url} alt={event.name}
              className="w-16 h-16 rounded-md object-cover flex-shrink-0" loading="lazy" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm leading-snug">{event.name}</p>
            {(event.short_description || event.tagline) && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2" data-testid="vbs-res-summary-desc">
                {event.short_description || event.tagline}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">
              {depositPerGuestCents > 0
                ? `Prepaid reservation for parties of ${partyMin} to ${partyMax}`
                : `Reservation for parties of ${partyMin} to ${partyMax}`}
            </p>
            {depositPerGuestCents > 0 && (
              <p className="text-xs text-fuchsia-400 font-bold mt-1">
                {largeThreshold
                  ? `${fmtMoney(depositPerGuestCents)}/person for parties of ${largeThreshold}+`
                  : `${fmtMoney(depositPerGuestCents)} per person`}
              </p>
            )}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2">
            <Users className="h-3 w-3" /> Party size
          </Label>
          <div className="flex items-center gap-3">
            <Button size="icon" variant="outline" className="h-9 w-9"
              onClick={() => setParty((p) => Math.max(partyMin, p - 1))}
              disabled={party <= partyMin}
              data-testid="vbs-res-party-minus">
              <Minus className="h-4 w-4" />
            </Button>
            <span className="font-bold text-lg w-8 text-center" data-testid="vbs-res-party">{party}</span>
            <Button size="icon" variant="outline" className="h-9 w-9"
              onClick={() => setParty((p) => Math.min(partyMax, p + 1))}
              disabled={party >= partyMax}
              data-testid="vbs-res-party-plus">
              <Plus className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground ml-2">{partyMin}–{partyMax} guests</span>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2">
            <Calendar className="h-3 w-3" /> Date
          </Label>
          <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
               data-testid="vbs-res-date-strip">
            {dates.map((d) => {
              const sel = date === d.iso;
              return (
                <button
                  key={d.iso}
                  type="button"
                  onClick={() => { setDate(d.iso); setTime(null); }}
                  data-testid={`vbs-res-date-${d.iso}`}
                  className={`flex-shrink-0 w-14 rounded-lg border py-2 text-center transition-colors ${
                    sel ? 'border-fuchsia-500 bg-fuchsia-500/10' :
                    'border-border hover:border-fuchsia-400/60'
                  }`}
                >
                  <div className={`text-[10px] uppercase tracking-wider ${sel ? 'text-fuchsia-300' : 'text-muted-foreground'}`}>{d.day}</div>
                  <div className="text-lg font-bold">{d.num}</div>
                  <div className="text-[10px] text-muted-foreground">{d.mon}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2">
            <Clock className="h-3 w-3" /> Available times
          </Label>
          {gridTimes.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">
              No times published yet — contact the venue directly.
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-1.5" data-testid="vbs-res-time-grid">
              {gridTimes.map((t) => {
                const sel = time === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTime(t)}
                    data-testid={`vbs-res-slot-${t}`}
                    className={`rounded-md border py-2 text-xs font-semibold transition-colors ${
                      sel
                        ? 'border-fuchsia-500 bg-fuchsia-500 text-white'
                        : 'border-border bg-card hover:border-fuchsia-400/60'
                    }`}
                  >
                    {fmtTime(t)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Button
          className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold"
          onClick={() => setStep('details')}
          disabled={!time}
          data-testid="vbs-res-next"
        >
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{event.name}</p>
          <p className="text-xs text-muted-foreground">
            {fmtDate(date)} · {fmtTime(time)} · {party} {party === 1 ? 'guest' : 'guests'}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="text-xs flex-shrink-0"
          onClick={() => setStep('search')}
          data-testid="vbs-res-back-to-search">
          <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Your Details</p>
        <div>
          <Label className="text-xs">Full name</Label>
          <Input data-testid="vbs-res-name" value={buyer.name}
            onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))} className="h-9" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" data-testid="vbs-res-email" value={buyer.email}
            onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))} className="h-9" />
        </div>
        <div>
          <Label className="text-xs">Phone (optional)</Label>
          <Input data-testid="vbs-res-phone" value={buyer.phone}
            onChange={(e) => setBuyer((b) => ({ ...b, phone: e.target.value }))} className="h-9" />
        </div>
        {(event.seating_areas || []).length > 0 && (
          <div>
            <Label className="text-xs">Seating area (optional)</Label>
            <select
              value={buyer.seating_area}
              onChange={(e) => setBuyer((b) => ({ ...b, seating_area: e.target.value }))}
              className="w-full h-9 rounded-md border border-border bg-muted text-foreground px-3 text-sm"
              data-testid="vbs-res-seating"
            >
              <option value="">No preference</option>
              {(event.seating_areas || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div>
          <Label className="text-xs">Special requests (optional)</Label>
          <Textarea data-testid="vbs-res-notes" value={buyer.special_requests}
            onChange={(e) => setBuyer((b) => ({ ...b, special_requests: e.target.value }))}
            className="min-h-[60px]" placeholder="Allergies, occasion, accessibility…" />
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div>
          <p className="text-xs text-muted-foreground">
            {depositActive
              ? `${party} × ${fmtMoney(depositPerGuestCents)} prepaid`
              : 'No deposit required'}
          </p>
          <p className="text-lg font-bold">
            {depositActive ? fmtMoney(depositTotalCents) : 'Free hold'}
          </p>
        </div>
        <Button
          className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold"
          onClick={handleReserve}
          disabled={submitting}
          data-testid="vbs-res-confirm"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> :
            depositActive ? <CreditCard className="h-4 w-4 mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          {depositActive ? 'Continue to Checkout' : 'Confirm Reservation'}
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Exported wrapper
// ──────────────────────────────────────────────────────────────────────
export default function VenueBookingSheet({ open, onOpenChange, target }) {
  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);
  if (!target) return null;
  const { kind, event } = target;
  const KindIcon = iconForKind(kind);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto bg-background"
        data-testid="venue-booking-sheet"
      >
        <SheetHeader className="text-left">
          <Button
            variant="ghost" size="sm"
            className="h-8 -ml-2 mb-1 self-start text-xs text-muted-foreground"
            onClick={close}
            data-testid="vbs-back"
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to venue
          </Button>
          <SheetTitle className="flex items-center gap-2 text-lg">
            <KindIcon className="h-5 w-5 text-fuchsia-400" />
            {ctaForKind(kind)}
          </SheetTitle>
          <SheetDescription className="text-xs">
            <span className="font-semibold text-foreground">{event.name}</span>
            {event.event_date && kind !== 'reservation' && (
              <> · {fmtDate(event.event_date)}{event.event_time ? ` · ${fmtTime(event.event_time)}` : ''}</>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {kind === 'experience'  && <ExperienceFlow  event={event} onClose={close} />}
          {kind === 'reservation' && <ReservationFlow event={event} onClose={close} />}
          {(kind === 'event' || !kind || kind === 'classic_event') && (
            <EventTicketFlow event={event} onClose={close} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Helper export: convert an array source name into a `kind` value.
// Used by callers iterating over profile.classic_events / experiences /
// reservations so they don't need to switch by source themselves.
export const kindFromSource = (source) => {
  if (source === 'experiences') return 'experience';
  if (source === 'reservations') return 'reservation';
  return 'event';
};
